import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams } from '@/services/core/listSearch'
import { sortParams, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'
import type { MoneyAmount } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

interface DeliveryStats {
  impressionDelivery: number
  renderedDelivery: number
  viewableDelivery: number
  clickDelivery: number
  spendDelivery: { currency: string; amount: number }
  viewability: number
  ctr: number
}

/**
 * An advertiser's own political budget for one election — the pre-campaign way
 * of declaring political spend, kept for advertisers that are not linked to a
 * `PoliticalCampaign`. The `campaigns` map on the API is keyed by topic name,
 * so its keys are the topics the advertiser may book against that election.
 */
export interface AdvertiserPoliticalBudget {
  electionId: string
  topics: string[]
  totalBudget: MoneyAmount | null
  /** The budget declared for each topic, keyed by topic name. */
  topicBudgets: Record<string, MoneyAmount | null>
}

/** The political budget as the API takes it: topic budgets keyed by topic name. */
export interface AdvertiserPoliticalBudgetInput {
  election: { id: string }
  totalBudget: MoneyAmount
  campaigns: Record<string, MoneyAmount>
}

/**
 * Who controls, and who pays for, the advertiser's political ads — when that
 * is someone other than the advertiser itself. Shown on transparency notices.
 */
export type AdvertiserEntityType = 'CONTROLLING' | 'PAYING'

export interface AdvertiserEntity {
  type: AdvertiserEntityType
  /** One of the advertiser `type` values, or '' when unset. */
  legalEntity: string
  legalName: string
  contactEmail: string
  url: string
  vatId: string
  address: AdvertiserAddress
}

export type AdvertiserEntityInput = Omit<AdvertiserEntity, 'legalEntity'> & { legalEntity?: string }

export interface AdvertiserAddress {
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postCode: string
  country: string
}

export interface Advertiser {
  id: string
  name: string
  description: string
  contact: string
  email: string
  phone: string
  legalName: string
  vatId: string
  advertiserUrl: string
  address: AdvertiserAddress
  billingId: string
  billingName: string
  billingSurname: string
  billingEmail: string
  billingPhone: string
  objectState: string
  /** Owning network — only populated by list queries that span networks. */
  ownerTeamId: string
  ownerTeamName: string
  defaultCurrency?: string
  labels: string[]
  advertiserType?: string
  bidInLastDay: boolean
  last24HourDeliveryStats: DeliveryStats | null
  last7DayDeliveryStats: DeliveryStats | null
  createTime: string
  validationWarnings: ValidationWarning[]
  /** Political campaigns the advertiser is linked to — the topics come from these. */
  politicalCampaignIds: string[]
  /** The same links with the publicId the anonymous transparency endpoints resolve them by. */
  politicalCampaigns: AdvertiserPoliticalCampaignRef[]
  /** Fallback topic source, for an advertiser with no linked campaign. */
  politicalBudgets: AdvertiserPoliticalBudget[]
  /** Controlling and paying entities, when not the advertiser itself. */
  entities: AdvertiserEntity[]
}

export interface AdvertiserPoliticalCampaignRef {
  id: string
  name: string
  publicId: string
}

export interface AdvertiserInput {
  name: string
  description?: string
  contact?: string
  email?: string
  phone?: string
  legalName?: string
  vatId?: string
  advertiserUrl?: string
  address?: Partial<AdvertiserAddress>
  billingId?: string
  billingName?: string
  billingSurname?: string
  billingEmail?: string
  billingPhone?: string
  objectState?: string
  ownerTeam?: { id: string }
  defaultCurrency?: string
  labels?: string[]
  type?: string | null
  /** Replaces the linked political campaigns; leave out to keep them. */
  politicalCampaigns?: { id: string }[]
  /** Replaces the political budgets; leave out to keep them. */
  politicalBudgets?: AdvertiserPoliticalBudgetInput[]
  /** Replaces the controlling and paying entities; leave out to keep them. */
  entities?: AdvertiserEntityInput[]
}

export interface AdvertiserInviteInput {
  name: string
  email: string
  subject?: string
}

/** An open request for a political organisation to link up with an advertiser. */
export interface PoliticalLinkRequest {
  /** Public id of the organisation — absent when it can no longer be resolved. */
  idOfPoliticalEntity?: string
  name: string
  expiry: string
}

/** Reads a reference sent either as a `{id}` object or as a bare id string. */
function refId(value: unknown): string {
  if (typeof value === 'string') return value
  return ((value as Record<string, unknown>)?.id as string) ?? ''
}

function mapMoney(value: unknown): MoneyAmount | null {
  const m = value as Record<string, unknown> | null | undefined
  if (!m || typeof m.amount !== 'number' || !m.currency) return null
  return { amount: m.amount, currency: m.currency as string }
}

function mapPoliticalBudgets(value: unknown): AdvertiserPoliticalBudget[] {
  return ((value as Record<string, unknown>[]) ?? [])
    .map((b) => {
      const campaigns = (b.campaigns as Record<string, unknown>) ?? {}
      return {
        // Older payloads carry the raw `electionId`; current ones a reference.
        electionId: refId(b.election) || String(b.electionId ?? ''),
        topics: Object.keys(campaigns),
        totalBudget: mapMoney(b.totalBudget),
        topicBudgets: Object.fromEntries(
          Object.entries(campaigns).map(([topic, money]) => [topic, mapMoney(money)])
        ),
      }
    })
    .filter((b) => !!b.electionId)
}

function mapAddress(address: Record<string, unknown> | undefined): AdvertiserAddress {
  return {
    addressLine1: (address?.addressLine1 as string) ?? '',
    addressLine2: (address?.addressLine2 as string) ?? '',
    city: (address?.city as string) ?? '',
    state: (address?.state as string) ?? '',
    postCode: (address?.postCode as string) ?? '',
    country: (address?.country as string) ?? '',
  }
}

function mapEntities(value: unknown): AdvertiserEntity[] {
  return ((value as Record<string, unknown>[]) ?? [])
    .filter((e) => e?.type === 'CONTROLLING' || e?.type === 'PAYING')
    .map((e) => ({
      type: e.type as AdvertiserEntityType,
      legalEntity: (e.legalEntity as string) ?? '',
      legalName: (e.legalName as string) ?? '',
      contactEmail: (e.contactEmail as string) ?? '',
      url: (e.url as string) ?? '',
      vatId: (e.vatId as string) ?? '',
      address: mapAddress(e.address as Record<string, unknown> | undefined),
    }))
}

function mapAdvertiser(r: Record<string, unknown>): Advertiser {
  const address = r.address as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    contact: (r.contact as string) ?? '',
    email: (r.email as string) ?? '',
    phone: (r.phone as string) ?? '',
    legalName: (r.legalName as string) ?? '',
    vatId: (r.vatId as string) ?? '',
    advertiserUrl: (r.advertiserUrl as string) ?? '',
    address: mapAddress(address),
    billingId: (r.billingId as string) ?? '',
    billingName: (r.billingName as string) ?? '',
    billingSurname: (r.billingSurname as string) ?? '',
    billingEmail: (r.billingEmail as string) ?? '',
    billingPhone: (r.billingPhone as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    ownerTeamId: ((r.ownerTeam as Record<string, unknown>)?.id as string) ?? '',
    ownerTeamName: ((r.ownerTeam as Record<string, unknown>)?.name as string) ?? '',
    defaultCurrency: (r.defaultCurrency as string) || undefined,
    labels: (r.labels as string[]) ?? [],
    advertiserType: (r.type as string) || undefined,
    bidInLastDay: (r.bidInLastDay as boolean) ?? false,
    last24HourDeliveryStats: (r.last24HourDeliveryStats as DeliveryStats) ?? null,
    last7DayDeliveryStats: (r.last7DayDeliveryStats as DeliveryStats) ?? null,
    createTime: (r.createTime as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    politicalCampaignIds: ((r.politicalCampaigns as Record<string, unknown>[]) ?? [])
      .map((c) => refId(c))
      .filter(Boolean),
    politicalCampaigns: ((r.politicalCampaigns as Record<string, unknown>[]) ?? [])
      .filter((c) => typeof c === 'object' && c?.publicId)
      .map((c) => ({ id: refId(c), name: (c.name as string) ?? '', publicId: c.publicId as string })),
    politicalBudgets: mapPoliticalBudgets(r.politicalBudgets),
    entities: mapEntities(r.entities),
  }
}

/**
 * Whether the advertiser has bid on, or delivered to, an auction recently —
 * what the list shows as "Bidding" or "Idle".
 *
 * The API cannot filter on the `bidInLastDay` flag itself: its `where` clause
 * takes no booleans. But the backend writes the flag and the 24-hour stats in
 * the same statement — stats present means bidding, nulled means not — so the
 * stats field's null-ness is the flag, and `is null` needs no value to compare.
 */
export type BiddingFilter = 'all' | 'bidding' | 'idle'

const BIDDING_WHERE: Record<BiddingFilter, string | null> = {
  all: null,
  bidding: 'last24HourDeliveryStats is not null',
  idle: 'last24HourDeliveryStats is null',
}

export interface AdvertiserPage {
  results: Advertiser[]
  totalCount: number
  page: number
  pageSize: number
}

export const AdvertisersApi = {
  getPage: async (
    page = 1,
    pageSize = 25,
    includeInactive = false,
    search?: string,
    bidding: BiddingFilter = 'all',
    sort?: ListSort | null
  ): Promise<AdvertiserPage> => {
    const where = BIDDING_WHERE[bidding]
    const params = new URLSearchParams({
      ...searchParams('advertisers', search),
      concise: 'true',
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: String(includeInactive),
      ...sortParams(sort, {
        orderBy: 'name',
        orderByDirection: 'ASCENDING',
        nullsOrderPolicy: 'FIRST',
      }),
      page: String(page),
      pageSize: String(pageSize),
      ...(where ? { where } : {}),
    })
    const res = await apiFetch(`${API_BASE}/advertisers?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch advertisers (${res.status})`)
    const data = await res.json()
    const results: Advertiser[] = (Array.isArray(data.results) ? data.results : []).map(mapAdvertiser)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /** @deprecated use getPage */
  getAll: async (): Promise<Advertiser[]> => {
    const page = await AdvertisersApi.getPage(1, 100)
    return page.results
  },

  getById: async (id: string): Promise<Advertiser> => {
    const res = await apiFetch(`${API_BASE}/advertisers/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch advertiser (${res.status})`)
    return mapAdvertiser(await res.json())
  },

  update: async (id: string, input: AdvertiserInput): Promise<Advertiser> => {
    const res = await apiFetch(`${API_BASE}/advertisers/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save advertiser')
    return mapAdvertiser(await res.json())
  },

  /**
   * Emails someone a time-limited link to update the advertiser's details —
   * through the political app when they already are a political user. The
   * advertiser must have an owner team. A blank subject uses the template's.
   */
  emailInvite: async (id: string, input: AdvertiserInviteInput): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/advertisers/emailinvite/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to send invite')
  },

  /**
   * Asks a political organisation to link up with the advertiser: the
   * political users of its owner team are emailed a link to the advertiser.
   * Only one request can be open per advertiser. A blank subject uses the
   * template's.
   */
  requestPoliticalLink: async (
    id: string,
    input: { idOfPoliticalEntity: string; subject?: string }
  ): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/advertisers/emailinvitepoliticalentity/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to send link request')
  },

  /** The advertiser's open political link request, if any. */
  getPoliticalLinkRequest: async (id: string): Promise<PoliticalLinkRequest | null> => {
    const res = await apiFetch(`${API_BASE}/advertisers/emailinvitepoliticalentity/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch link request')
    const data = await res.json()
    return data.invitation ?? null
  },

  /**
   * Withdraws the open political link request. The backend only offers DELETE
   * for this — the one exception to the GET/POST rule.
   */
  deletePoliticalLinkRequest: async (id: string): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/advertisers/emailinvitepoliticalentity/${id}?context=${getContext()}`, {
      method: 'DELETE',
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to remove link request')
  },
}
